<template>
  <div class="admin-manage">
    <!-- 统计卡片 -->
    <div class="stat-cards">
      <div class="stat-card">
        <div class="stat-icon" style="background: linear-gradient(135deg, #fa709a 0%, #fee140 100%)">
          <i class="el-icon-s-check"></i>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ adminList.length }}</div>
          <div class="stat-label">管理员总数</div>
        </div>
      </div>
    </div>

    <!-- 操作栏 -->
    <div class="toolbar">
      <div class="toolbar-left">
        <el-input
          v-model="searchKeyword"
          placeholder="搜索账号/用户名..."
          prefix-icon="el-icon-search"
          clearable
          class="search-input"
          @input="handleSearch"
        ></el-input>
      </div>
      <div class="toolbar-right">
        <el-button class="action-btn" @click="handleAdd">
          <i class="el-icon-plus"></i>
          <span>新增管理员</span>
        </el-button>
      </div>
    </div>

    <!-- 管理员列表 -->
    <el-table
      :data="filteredAdminList"
      v-loading="loading"
      class="modern-table"
    >
      <el-table-column prop="account" label="登录账号" min-width="180">
        <template v-slot="scope">
          <div class="account-cell">
            <el-avatar :size="36" :src="scope.row.portrait || defaultAvatar" class="account-avatar" />
            <div class="account-info">
              <div class="account-name">{{ scope.row.account }}</div>
              <div class="account-id">ID: {{ scope.row._id | shortId }}</div>
            </div>
          </div>
        </template>
      </el-table-column>
      <el-table-column prop="userName" label="用户名" min-width="150">
        <template v-slot="scope">
          <span class="username-text">{{ scope.row.userName || '-' }}</span>
        </template>
      </el-table-column>
      <el-table-column label="头像" width="80" align="center">
        <template v-slot="scope">
          <el-avatar :size="32" :src="scope.row.portrait || defaultAvatar" />
        </template>
      </el-table-column>
      <el-table-column prop="createdAt" label="创建时间" width="170" align="center">
        <template v-slot="scope">
          <span class="time-text">
            <i class="el-icon-time"></i>
            {{ scope.row.createdAt | formatDate('YYYY-MM-DD HH:mm') }}
          </span>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="180" fixed="right" align="center">
        <template v-slot="scope">
          <div class="action-btns">
            <el-button type="primary" plain size="mini" @click="handleEdit(scope.row)">
              <i class="el-icon-edit"></i> 编辑
            </el-button>
            <el-button type="danger" plain size="mini" @click="handleDelete(scope.row)" :disabled="adminList.length <= 1">
              <i class="el-icon-delete"></i> 删除
            </el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <el-pagination
      v-if="total > 20"
      class="modern-pagination"
      layout="total, prev, pager, next"
      :total="total"
      :page-size="20"
      @current-change="handlePageChange"
    ></el-pagination>

    <!-- 新增/编辑对话框 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="520px"
      top="15vh"
      class="admin-dialog"
      :close-on-click-modal="false"
      @close="handleDialogClose"
    >
      <div class="dialog-content">
        <el-form ref="adminForm" :model="adminForm" :rules="rules" label-width="90px" label-position="right">
          <el-form-item label="登录账号" prop="account">
            <el-input
              v-model="adminForm.account"
              placeholder="请输入登录账号（至少8个字符）"
              :disabled="!!editId"
              maxlength="50"
              prefix-icon="el-icon-user"
            ></el-input>
          </el-form-item>
          <el-form-item :label="editId ? '新密码' : '密码'" :prop="editId ? '' : 'password'">
            <el-input
              v-model="adminForm.password"
              type="password"
              show-password
              :placeholder="editId ? '留空表示不修改密码' : '请输入密码（6-18位）'"
              maxlength="18"
              prefix-icon="el-icon-lock"
            ></el-input>
          </el-form-item>
          <el-form-item label="用户名" prop="userName">
            <el-input
              v-model="adminForm.userName"
              placeholder="请输入显示的用户名"
              maxlength="50"
              prefix-icon="el-icon-info"
            ></el-input>
          </el-form-item>
          <el-form-item label="头像">
            <div class="avatar-upload-wrapper">
              <el-avatar :size="64" :src="adminForm.portrait || defaultAvatar" class="preview-avatar" />
              <fa-image-upload
                v-model="adminForm.portrait"
                :limit="1"
                class="avatar-upload"
              />
            </div>
          </el-form-item>
        </el-form>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button class="cancel-btn" @click="dialogVisible = false">
          <i class="el-icon-close"></i> 取消
        </el-button>
        <el-button type="primary" class="confirm-btn" :loading="submitLoading" @click="handleSubmit">
          <i class="el-icon-check"></i> {{ editId ? '保存修改' : '确认创建' }}
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getAdminList, addAdmin, editAdmin, deleteAdmin } from '@/api/admin'

const defaultAvatar = 'https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png'

export default {
  name: 'admin-manage',
  data() {
    return {
      adminList: [],
      loading: false,
      total: 0,
      page: 1,
      dialogVisible: false,
      dialogTitle: '新增管理员',
      editId: '',
      submitLoading: false,
      searchKeyword: '',
      adminForm: {
        account: '',
        password: '',
        userName: '',
        portrait: ''
      },
      rules: {
        account: [
          { required: true, message: '请输入登录账号', trigger: 'blur' },
          { min: 8, message: '账号长度不能少于8个字符', trigger: 'blur' }
        ],
        password: [
          { min: 6, max: 18, message: '密码长度必须在6-18位之间', trigger: 'blur' }
        ],
        userName: [{ required: true, message: '请输入用户名', trigger: 'blur' }]
      },
      defaultAvatar
    }
  },
  computed: {
    filteredAdminList() {
      if (!this.searchKeyword) return this.adminList
      const kw = this.searchKeyword.toLowerCase()
      return this.adminList.filter(a =>
        a.account.toLowerCase().includes(kw) ||
        (a.userName && a.userName.toLowerCase().includes(kw))
      )
    }
  },
  filters: {
    shortId(id) {
      if (!id) return ''
      return id.slice(-6).toUpperCase()
    }
  },
  created() {
    this.fetchList()
  },
  methods: {
    handleSearch() {},
    async fetchList() {
      this.loading = true
      try {
        const res = await getAdminList({ page: this.page, pageSize: 20 })
        if (res.status === '10000') {
          this.adminList = res.list || []
          this.total = res.total || 0
        }
      } catch (e) {
        this.$message.error('获取管理员列表失败')
      }
      this.loading = false
    },
    handleAdd() {
      this.dialogTitle = '新增管理员'
      this.editId = ''
      this.adminForm = { account: '', password: '', userName: '', portrait: '' }
      this.dialogVisible = true
    },
    handleEdit(row) {
      this.dialogTitle = '编辑管理员'
      this.editId = row._id
      this.adminForm = {
        account: row.account,
        password: '',
        userName: row.userName,
        portrait: row.portrait || ''
      }
      this.dialogVisible = true
    },
    async handleDelete(row) {
      try {
        await this.$confirm('确定要删除该管理员吗？删除后该账号将无法登录。', '删除确认', {
          type: 'warning',
          confirmButtonText: '确定删除',
          cancelButtonText: '取消'
        })
        const res = await deleteAdmin({ id: row._id })
        if (res.status === '10000') {
          this.$message.success('删除成功')
          this.fetchList()
        } else {
          this.$message.error(res.message || '删除失败')
        }
      } catch (e) {
        if (e !== 'cancel') this.$message.error('删除失败')
      }
    },
    handleSubmit() {
      this.$refs.adminForm.validate(async (valid) => {
        if (!valid) return
        this.submitLoading = true
        const data = { ...this.adminForm }
        if (this.editId) {
          data.id = this.editId
          if (!data.password) delete data.password
        }
        try {
          let res
          if (this.editId) {
            res = await editAdmin(data)
          } else {
            res = await addAdmin(data)
          }
          if (res.status === '10000') {
            this.$message.success(this.editId ? '编辑成功' : '创建成功')
            this.dialogVisible = false
            this.fetchList()
          } else {
            this.$message.error(res.message || '操作失败')
          }
        } catch (e) {
          this.$message.error('操作失败')
        }
        this.submitLoading = false
      })
    },
    handleDialogClose() {
      this.$refs.adminForm && this.$refs.adminForm.resetFields()
    },
    handlePageChange(page) {
      this.page = page
      this.fetchList()
    }
  }
}
</script>

<style lang="scss" scoped>
.admin-manage {
  padding: 20px 0;

  // ==================== 统计卡片 ====================
  .stat-cards {
    display: flex;
    gap: 16px;
    margin-bottom: 20px;

    .stat-card {
      display: flex;
      align-items: center;
      flex: 1;
      padding: 20px;
      background: #fff;
      border-radius: 12px;
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
      transition: all 0.3s ease;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
      }

      .stat-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 56px;
        height: 56px;
        border-radius: 12px;
        margin-right: 16px;

        i {
          font-size: 28px;
          color: #fff;
        }
      }

      .stat-content {
        .stat-value {
          font-size: 28px;
          font-weight: 600;
          color: #303133;
          line-height: 1.2;
        }

        .stat-label {
          font-size: 13px;
          color: #909399;
          margin-top: 4px;
        }
      }
    }
  }

  // ==================== 工具栏 ====================
  .toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
    padding: 16px 20px;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

    .toolbar-left {
      .search-input {
        width: 280px;

        ::v-deep .el-input__inner {
          border-radius: 20px;
          padding-left: 36px;
        }
      }
    }

    .toolbar-right {
      .action-btn {
        background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
        border: none;
        border-radius: 20px;
        padding: 10px 20px;
        color: #fff;
        font-weight: 500;
        transition: all 0.3s ease;

        &:hover {
          opacity: 0.9;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(250, 112, 154, 0.4);
        }

        i {
          margin-right: 4px;
        }
      }
    }
  }

  // ==================== 表格 ====================
  ::v-deep .modern-table {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);

    .el-table__header th {
      background: linear-gradient(135deg, #f5f7fa 0%, #e8eef5 100%);
      color: #606266;
      font-weight: 600;
      padding: 14px 0;
    }

    .el-table__row {
      transition: all 0.2s ease;

      &:hover {
        background: #fff8f0 !important;
      }
    }

    .el-table__body td {
      padding: 12px 0;
    }
  }

  .account-cell {
    display: flex;
    align-items: center;

    .account-avatar {
      margin-right: 12px;
      border: 2px solid #e8eef5;
    }

    .account-info {
      .account-name {
        font-weight: 500;
        color: #303133;
        font-size: 14px;
      }

      .account-id {
        font-size: 12px;
        color: #c0c4cc;
        margin-top: 2px;
      }
    }
  }

  .username-text {
    color: #606266;
    font-size: 14px;
  }

  .time-text {
    color: #909399;
    font-size: 13px;

    i {
      margin-right: 4px;
      color: #c0c4cc;
    }
  }

  .action-btns {
    display: flex;
    gap: 8px;
    justify-content: center;

    .el-button {
      border-radius: 6px;
      padding: 6px 12px;
      font-size: 12px;

      &.el-button--primary {
        &:hover {
          background: #fa709a;
          border-color: #fa709a;
          color: #fff;
        }
      }

      &.el-button--danger {
        &:hover {
          background: #f56c6c;
          border-color: #f56c6c;
          color: #fff;
        }
      }

      &[disabled] {
        opacity: 0.4;
      }
    }
  }

  // ==================== 分页 ====================
  .modern-pagination {
    margin-top: 20px;
    padding: 12px 20px;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    text-align: right;

    ::v-deep .el-pagination {
      .btn-prev, .btn-next, .el-pager li {
        border-radius: 6px;
        min-width: 32px;
      }

      .el-pager li.active {
        background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
        color: #fff;
      }
    }
  }
}

// ==================== 弹窗样式 ====================
::v-deep .admin-dialog {
  border-radius: 16px;
  overflow: hidden;

  .el-dialog__header {
    background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
    padding: 20px 24px;
    margin: 0;

    .el-dialog__title {
      color: #fff;
      font-size: 18px;
      font-weight: 600;
    }

    .el-dialog__headerbtn .el-dialog__close {
      color: #fff;
      font-size: 20px;

      &:hover {
        color: #f0f0f0;
      }
    }
  }

  .el-dialog__body {
    padding: 24px;
    max-height: 65vh;
    overflow-y: auto;
  }

  .el-form-item {
    margin-bottom: 20px;
  }

  .el-dialog__footer {
    padding: 16px 24px;
    border-top: 1px solid #ebeef5;
    background: #fafbfc;
  }

  .dialog-footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;

    .cancel-btn {
      border-radius: 8px;
      padding: 10px 24px;

      i {
        margin-right: 4px;
      }
    }

    .confirm-btn {
      border-radius: 8px;
      padding: 10px 28px;
      background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
      border: none;
      font-weight: 500;

      i {
        margin-right: 4px;
      }

      &:hover {
        opacity: 0.9;
        box-shadow: 0 4px 12px rgba(250, 112, 154, 0.4);
      }
    }
  }
}

// ==================== 头像上传 ====================
.avatar-upload-wrapper {
  display: flex;
  align-items: center;
  gap: 16px;

  .preview-avatar {
    border: 2px solid #e8eef5;
  }

  .avatar-upload {
    flex: 1;
  }
}
</style>
